import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, FormControl, Select, MenuItem,
  CircularProgress, Alert, Skeleton, IconButton, Tooltip, Drawer,
  Table, TableBody, TableCell, TableHead, TableRow, Divider,
} from "@mui/material";
import RefreshRoundedIcon        from "@mui/icons-material/RefreshRounded";
import ReceiptLongRoundedIcon     from "@mui/icons-material/ReceiptLongRounded";
import CheckCircleRoundedIcon     from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon    from "@mui/icons-material/WarningAmberRounded";
import ErrorRoundedIcon           from "@mui/icons-material/ErrorRounded";
import TrackChangesRoundedIcon    from "@mui/icons-material/TrackChangesRounded";
import OpenInNewRoundedIcon       from "@mui/icons-material/OpenInNewRounded";
import CloseRoundedIcon           from "@mui/icons-material/CloseRounded";
import Inventory2RoundedIcon      from "@mui/icons-material/Inventory2Rounded";
import { getData, API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

// ── Trip status config ────────────────────────────────────────────────────
const TRIP_STATUS = {
  green: { label:"Compliant",    bg:T.emeraldLt, color:T.emerald, icon:<CheckCircleRoundedIcon /> },
  amber: { label:"Pre-trip gap", bg:T.amberLt,   color:T.amber,   icon:<WarningAmberRoundedIcon /> },
  red:   { label:"Critical",     bg:T.roseLt,    color:T.rose,    icon:<ErrorRoundedIcon /> },
};

// ── CEN status config ─────────────────────────────────────────────────────
const CEN_STATUS = {
  none:                { label:"—",          bg:"#F1F5F9",  color:T.muted   },
  valid:               { label:"Present",    bg:T.emeraldLt,color:T.emerald },
  expired:             { label:"Expired",    bg:T.roseLt,   color:T.rose    },
  coverage_incomplete: { label:"Incomplete", bg:T.amberLt,  color:T.amber   },
  stale_due_to_change: { label:"Stale",      bg:T.amberLt,  color:T.amber   },
};

// ── EWB package status ────────────────────────────────────────────────────
const pkgEwbStatus = (m) => {
  if (!m.ewb_required) return { label:"Exempt",  bg:"#F1F5F9", color:T.muted   };
  if (!m.ewb_number)   return { label:"Missing", bg:T.roseLt,  color:T.rose    };
  if (m.ewb_valid_until && new Date(m.ewb_valid_until) < new Date())
    return { label:"Expired", bg:T.amberLt, color:T.amber };
  return { label:"Present", bg:T.emeraldLt, color:T.emerald };
};

const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const fmtDateT = d => d ? new Date(d).toLocaleString("en-IN",   { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

// ── Mini chip ─────────────────────────────────────────────────────────────
const MChip = ({ label, bg, color }) => (
  <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:bg, border:`1px solid ${color}22` }}>
    <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:color }} />
    <Typography sx={{ fontSize:11, fontWeight:700, color, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{label}</Typography>
  </Box>
);

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, icon, accent, iconBg, loading }) => (
  <Card sx={{ flex:"1 1 140px", borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
    <Box sx={{ height:3, background:`linear-gradient(90deg,${accent},${accent}66)` }} />
    <CardContent sx={{ p:2, "&:last-child":{ pb:2 } }}>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <Box>
          <Typography sx={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", mb:0.8 }}>{label}</Typography>
          {loading ? <Skeleton width={50} height={28} /> :
            <Typography sx={{ fontSize:24, fontWeight:800, color:accent, lineHeight:1.1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{value}</Typography>}
        </Box>
        <Box sx={{ width:38, height:38, borderRadius:"10px", bgcolor:iconBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {React.cloneElement(icon, { sx:{ fontSize:18, color:accent } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Skeleton row ──────────────────────────────────────────────────────────
const SkelRow = () => (
  <TableRow>
    {[60,100,70,70,70,90,70,70,90].map((w,i)=><TableCell key={i}><Skeleton width={w} height={16} /></TableCell>)}
  </TableRow>
);

// ── Package Drawer ────────────────────────────────────────────────────────
function PackageDrawer({ tripId, open, onClose }) {
  const [pkgs,    setPkgs]    = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !tripId) return;
    setLoading(true);
    const url = API_ENDPOINTS.ewbOwnerTripBookings.replace(":trip_id", tripId);
    getData(url)
      .then(r => {
        const all = r?.result?.bookings || [];
        // Show only EWB-required packages
        setPkgs(all.filter(m => m.ewb_required));
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [open, tripId]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx:{ width: { xs:"100%", sm:560 }, bgcolor:T.bg } }}>
      {/* Header */}
      <Box sx={{ bgcolor:`linear-gradient(135deg,${T.teal},${T.tealDk})`, background:`linear-gradient(135deg,${T.teal},${T.tealDk})`, p:2.5 }}>
        <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
            <Box sx={{ width:36, height:36, borderRadius:"10px", bgcolor:"rgba(255,255,255,0.18)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Inventory2RoundedIcon sx={{ fontSize:18, color:"#fff" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize:16, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                EWB Packages — Trip #{tripId}
              </Typography>
              <Typography sx={{ fontSize:12, color:"rgba(255,255,255,0.75)" }}>
                Packages requiring E-Way Bill
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color:"rgba(255,255,255,0.8)", "&:hover":{ bgcolor:"rgba(255,255,255,0.15)" } }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p:2, overflow:"auto", flex:1 }}>
        {loading ? (
          Array.from({length:4}).map((_,i) => (
            <Card key={i} sx={{ borderRadius:"12px", border:`1px solid ${T.border}`, mb:1.5 }}>
              <CardContent sx={{ p:2 }}><Skeleton height={60} /></CardContent>
            </Card>
          ))
        ) : pkgs.length === 0 ? (
          <Box sx={{ textAlign:"center", py:6 }}>
            <Inventory2RoundedIcon sx={{ fontSize:40, color:T.border, mb:1 }} />
            <Typography sx={{ color:T.muted, fontSize:13 }}>No EWB-required packages found for this trip.</Typography>
          </Box>
        ) : (
          <>
            {/* Summary */}
            <Box sx={{ display:"flex", gap:1.5, mb:2, flexWrap:"wrap" }}>
              {[
                { label:`${pkgs.length} required`, color:T.teal, bg:T.tealLt },
                { label:`${pkgs.filter(m=>!m.ewb_number).length} missing`, color:T.rose, bg:T.roseLt },
                { label:`${pkgs.filter(m=>m.ewb_number).length} present`, color:T.emerald, bg:T.emeraldLt },
              ].map(s => (
                <Box key={s.label} sx={{ px:1.5, py:0.6, bgcolor:s.bg, borderRadius:"9px", border:`1px solid ${s.color}22` }}>
                  <Typography sx={{ fontSize:12, fontWeight:700, color:s.color }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Package cards */}
            {pkgs.map(m => {
              const ewbSt = pkgEwbStatus(m);
              return (
                <Card key={m.mapping_id} sx={{ borderRadius:"12px", border:`1px solid ${T.border}`, mb:1.5, overflow:"hidden",
                  boxShadow:"0 1px 3px rgba(15,23,42,0.05)" }}>
                  <Box sx={{ height:2.5, bgcolor:ewbSt.color }} />
                  <CardContent sx={{ p:2, "&:last-child":{ pb:2 } }}>
                    <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:1 }}>
                      <Box>
                        <Typography sx={{ fontSize:13, fontWeight:700, color:T.text }}>{m.Package?.package_name || `Package #${m.package_id}`}</Typography>
                        <Box sx={{ display:"flex", alignItems:"center", gap:1, mt:0.4, flexWrap:"wrap" }}>
                          {m.Package?.tracking_code && (
                            <Box sx={{ display:"inline-flex", px:1, py:0.2, bgcolor:T.tealLt, borderRadius:"6px", border:`1px solid ${T.teal}22` }}>
                              <Typography sx={{ fontSize:10, fontWeight:700, color:T.teal, fontFamily:"monospace" }}>{m.Package.tracking_code}</Typography>
                            </Box>
                          )}
                          <Typography sx={{ fontSize:11, color:T.muted }}>Mapping #{m.mapping_id} · Pkg #{m.package_id}</Typography>
                        </Box>
                      </Box>
                      <MChip label={ewbSt.label} bg={ewbSt.bg} color={ewbSt.color} />
                    </Box>
                    <Divider sx={{ mb:1, borderColor:T.border }} />
                    <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:1 }}>
                      {[
                        { label:"EWB Number",   value: m.ewb_number || "—" },
                        { label:"Valid Until",  value: fmtDate(m.ewb_valid_until) },
                        { label:"Exempt",       value: m.ewb_exempt_reason || (m.ewb_required ? "No" : "Yes") },
                        { label:"Invoice #",    value: m.invoice_number || "—" },
                      ].map(f => (
                        <Box key={f.label}>
                          <Typography sx={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.3px" }}>{f.label}</Typography>
                          <Typography sx={{ fontSize:12, fontWeight:600, color:T.text, fontFamily:f.label==="EWB Number"?"monospace":"inherit" }}>{f.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}
      </Box>
    </Drawer>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function EwbComplianceDashboard() {
  const [filter,    setFilter]    = useState("all");
  const [cenFilter, setCenFilter] = useState("all");
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [drawer,    setDrawer]    = useState({ open:false, tripId:null });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filter    !== "all") params.status = filter;
      if (cenFilter !== "all") params.cen    = cenFilter;
      const res = await getData(API_ENDPOINTS.ewbAdminComplianceDashboard, params);
      setData(res?.result || null);
    } catch(e) { setError(e.response?.data?.message || e.message); }
    setLoading(false);
  }, [filter, cenFilter]);

  useEffect(() => { load(); }, [load]);

  const sum = data?.summary || {};
  const cen = data?.cen_summary || {};

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <ReceiptLongRoundedIcon sx={{ fontSize:24, color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>EWB Compliance Dashboard</Typography>
            <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.3 }}>
              EWB-001 · Per-trip status · last 30 days · trips with no EWB-required packages counted as compliant
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={load} sx={{ bgcolor:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:"10px", color:"#fff", "&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
            <RefreshRoundedIcon sx={{ fontSize:18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb:2, borderRadius:"12px" }} onClose={()=>setError(null)}>{error}</Alert>}

      {/* ── KPI tiles ── */}
      <Box sx={{ display:"flex", gap:2, flexWrap:"wrap", mb:3 }}>
        <KpiTile label="Compliant"    value={loading?"—":sum.green??0}  icon={<CheckCircleRoundedIcon />}  accent={T.emerald} iconBg={T.emeraldLt} loading={loading} />
        <KpiTile label="Pre-trip Gap" value={loading?"—":sum.amber??0}  icon={<WarningAmberRoundedIcon />} accent={T.amber}   iconBg={T.amberLt}   loading={loading} />
        <KpiTile label="Critical"     value={loading?"—":sum.red??0}    icon={<ErrorRoundedIcon />}        accent={T.rose}    iconBg={T.roseLt}    loading={loading} />
        <KpiTile label="Total Tracked" value={loading?"—":sum.total??0} icon={<TrackChangesRoundedIcon />} accent={T.indigo}  iconBg={T.indigoLt}  loading={loading} />
      </Box>

      {/* ── CEN summary strip ── */}
      {data?.cen_summary && (
        <Card sx={{ borderRadius:"14px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", mb:3 }}>
          <CardContent sx={{ p:2, "&:last-child":{ pb:2 }, display:"flex", alignItems:"center", gap:2, flexWrap:"wrap" }}>
            <Typography sx={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px" }}>Consolidated EWB</Typography>
            {[
              { label:`${cen.present??0} Present`,    color:T.emerald, bg:T.emeraldLt },
              { label:`${cen.missing??0} Missing`,    color:T.muted,   bg:"#F1F5F9"  },
              { label:`${cen.incomplete??0} Incomplete`, color:T.amber, bg:T.amberLt },
              { label:`${cen.stale??0} Stale`,        color:T.amber,   bg:T.amberLt  },
              { label:`${cen.expired??0} Expired`,    color:T.rose,    bg:T.roseLt   },
            ].map(s => (
              <Box key={s.label} sx={{ px:1.5, py:0.5, bgcolor:s.bg, borderRadius:"8px", border:`1px solid ${s.color}22` }}>
                <Typography sx={{ fontSize:12, fontWeight:700, color:s.color }}>{s.label}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Filters ── */}
      <Box sx={{ display:"flex", gap:1.5, flexWrap:"wrap", mb:2.5 }}>
        <FormControl size="small">
          <Select value={filter} displayEmpty onChange={e=>setFilter(e.target.value)}
            sx={{ borderRadius:"12px", bgcolor:"white", fontSize:13, minWidth:180 }}>
            <MenuItem value="all"><em style={{ color:T.muted }}>All Statuses</em></MenuItem>
            <MenuItem value="red"   sx={{ fontSize:13 }}>Critical (red)</MenuItem>
            <MenuItem value="amber" sx={{ fontSize:13 }}>Pre-trip gap (amber)</MenuItem>
            <MenuItem value="green" sx={{ fontSize:13 }}>Compliant (green)</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small">
          <Select value={cenFilter} displayEmpty onChange={e=>setCenFilter(e.target.value)}
            sx={{ borderRadius:"12px", bgcolor:"white", fontSize:13, minWidth:200 }}>
            <MenuItem value="all"><em style={{ color:T.muted }}>All CEN Statuses</em></MenuItem>
            <MenuItem value="missing"    sx={{ fontSize:13 }}>Missing</MenuItem>
            <MenuItem value="present"    sx={{ fontSize:13 }}>Present (valid)</MenuItem>
            <MenuItem value="incomplete" sx={{ fontSize:13 }}>Incomplete coverage</MenuItem>
            <MenuItem value="stale"      sx={{ fontSize:13 }}>Stale</MenuItem>
            <MenuItem value="expired"    sx={{ fontSize:13 }}>Expired</MenuItem>
          </Select>
        </FormControl>
        {!loading && data?.page && (
          <Typography sx={{ fontSize:13, color:T.muted, ml:"auto", alignSelf:"center" }}>
            {data.page.returned} of {data.page.filtered} trips · since {fmtDate(data.since)}
          </Typography>
        )}
      </Box>

      {/* ── Trips table ── */}
      <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor:T.bg }}>
              {["Trip","Status","Required","Missing","Expired","CEN","Started","Completed","Created",""].map(h=>(
                <TableCell key={h} sx={{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.4px",py:1.5,borderBottom:`2px solid ${T.border}` }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({length:5}).map((_,i)=><SkelRow key={i} />)
              : (data?.trips||[]).length===0
              ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py:6 }}>
                  <ReceiptLongRoundedIcon sx={{ fontSize:40,color:T.border,mb:1,display:"block",mx:"auto" }} />
                  <Typography fontSize={13} color="text.secondary">No trips match this filter</Typography>
                </TableCell></TableRow>
              )
              : (data?.trips||[]).map(t => {
                const sc = TRIP_STATUS[t.status] || { label:t.status, bg:"#F1F5F9", color:T.muted };
                const cenSt = t.consolidated_ewb?.status || "none";
                const cn = CEN_STATUS[cenSt] || { label:cenSt, bg:"#F1F5F9", color:T.muted };
                return (
                  <TableRow key={t.trip_id} hover
                    sx={{ cursor:"pointer","&:last-child td":{ border:0 },"&:hover":{ bgcolor:T.tealLt+"33" } }}
                    onClick={()=>setDrawer({ open:true, tripId:t.trip_id })}>

                    {/* Trip # */}
                    <TableCell>
                      <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.5,px:1,py:0.3,bgcolor:T.bg,border:`1px solid ${T.border}`,borderRadius:"6px" }}>
                        <Typography sx={{ fontSize:12,fontWeight:700,color:T.teal,fontFamily:"monospace" }}>#{t.trip_id}</Typography>
                      </Box>
                    </TableCell>

                    {/* Status */}
                    <TableCell><MChip label={sc.label} bg={sc.bg} color={sc.color} /></TableCell>

                    {/* Counts */}
                    <TableCell><Typography sx={{ fontSize:13,fontWeight:700,color:T.text }}>{t.required_count}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize:13,fontWeight:700,color:t.missing_count>0?T.rose:T.text }}>{t.missing_count}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize:13,fontWeight:700,color:t.expired_count>0?T.amber:T.text }}>{t.expired_count}</Typography></TableCell>

                    {/* CEN */}
                    <TableCell>
                      <MChip label={cn.label} bg={cn.bg} color={cn.color} />
                      {t.consolidated_ewb?.number && (
                        <Typography sx={{ fontSize:10,color:T.muted,mt:0.3,fontFamily:"monospace" }}>
                          {t.consolidated_ewb.number}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Started/Completed */}
                    <TableCell>
                      <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.4,px:0.8,py:0.2,bgcolor:t.trip_started?T.emeraldLt:"#F1F5F9",borderRadius:"6px" }}>
                        <Typography sx={{ fontSize:11,fontWeight:700,color:t.trip_started?T.emerald:T.muted }}>{t.trip_started?"Yes":"No"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.4,px:0.8,py:0.2,bgcolor:t.trip_completed?T.tealLt:"#F1F5F9",borderRadius:"6px" }}>
                        <Typography sx={{ fontSize:11,fontWeight:700,color:t.trip_completed?T.teal:T.muted }}>{t.trip_completed?"Yes":"No"}</Typography>
                      </Box>
                    </TableCell>

                    <TableCell><Typography sx={{ fontSize:12,color:T.muted }}>{fmtDate(t.created_at)}</Typography></TableCell>

                    {/* View packages */}
                    <TableCell padding="none">
                      <Tooltip title="View EWB packages">
                        <IconButton size="small" onClick={e=>{e.stopPropagation();setDrawer({ open:true, tripId:t.trip_id });}}
                          sx={{ color:T.muted }}>
                          <OpenInNewRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </Card>

      {/* ── Package drawer ── */}
      <PackageDrawer
        tripId={drawer.tripId}
        open={drawer.open}
        onClose={()=>setDrawer({ open:false, tripId:null })}
      />
    </Box>
  );
}
